import React, { useEffect, useRef, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { diff } from 'deep-object-diff';
import { saveAs as saveAsFile } from 'file-saver';
import AddIcon from 'icons/add-new.svg';
import ArchiveIcon from 'icons/archive.svg';
import ChevronDownIcon from 'icons/chevron-down-new.svg';
import ChevronUpIcon from 'icons/chevron-up-new.svg';
import CreateNewFolderIcon from 'icons/create-new-folder.svg';
import DriveFileMoveIcon from 'icons/drive-file-move.svg';
import EditIcon from 'icons/edit.svg';
import FilesIcon from 'icons/files.svg';
import FunnelIcon from 'icons/funnel.svg';
import CloseIcon from 'icons/ic-close.svg';
import TableIcon from 'icons/ic-table.svg';
import LoadingIcon from 'icons/loading.svg';
import NoteAdd from 'icons/note-add.svg';
import SearchOffIcon from 'icons/search-off.svg';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import voca from 'voca';

import { useUpdateFolderMutation } from 'components/hooks/folderMutations';
import { useFolderQuery } from 'components/hooks/folderQueries';
import { useCreateFoldersUserSelectionMutation } from 'components/hooks/foldersUserSelectionMutations';
import {
  useCreateProjectArchiveMutation,
  useCreateProjectRestorationMutation,
  useDestroyProjectMutation,
} from 'components/hooks/projectMutations';
import { useProjectsDashboardsProjectsReportsDashboardReportQuery } from 'components/hooks/projectsDashboardsProjectsReportsDashboardReportQueries';
import { useCreateProjectsDashboardsProjectsReportsExportProcessMutation } from 'components/hooks/projectsDashboardsProjectsReportsExportProcessMutations';
import { useProjectsReportsExportProcessQuery } from 'components/hooks/projectsReportsExportProcessQueries';
import useDebounce from 'components/hooks/useDebounce';
import useForm from 'components/hooks/useForm';

import FilledButton from 'components/application/buttons/FilledButton';
import ConditionalWrapper from 'components/application/ConditionalWrapper';
import OptionChoice from 'components/application/OptionChoice';
import Options from 'components/application/Options';
import Paginator from 'components/application/Paginator';
import SearchField from 'components/application/SearchField';
import SegmentedControls from 'components/application/SegmentedControls';
import SuccessToast from 'components/application/SuccessToast';
import Tooltip from 'components/application/Tooltip';
import UndoableToast from 'components/application/UndoableToast';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsDashboardContext } from 'components/contexts/ProjectsDashboardContext';
import { useBreadBoard } from 'components/contexts/Toaster';
import ArchiveProjectModal from 'components/projectsDashboard/components/ArchiveProjectModal';
import DestroyProjectModal from 'components/projectsDashboard/components/DestroyProjectModal';
import DocumentAllowanceLimitReachedModal from 'components/projectsDashboard/components/DocumentAllowanceLimitReachedModal';
import EditColumnsSidePanel from 'components/projectsDashboard/components/EditColumnsSidePanel';
import EmptyTableState from 'components/projectsDashboard/components/EmptyTableState';
import ExportProjectsModal from 'components/projectsDashboard/components/ExportProjectsModal';
import FavouriteFolderToggleButton from 'components/projectsDashboard/components/FavouriteFolderToggleButton';
import ProjectDuplicationManager from 'components/projectsDashboard/components/ProjectDuplicationManager';
import ProjectsFilterSidePanel from 'components/projectsDashboard/components/ProjectsFilterSidePanel';
import ProjectsTable from 'components/projectsDashboard/components/ProjectsTable';
import ResetTableToDefaultConfirmationModal from 'components/projectsDashboard/components/ResetTableToDefaultConfirmationModal';
import {
  buildFilterParams,
  buildSelectionsParams,
  buildSortParams,
  defaultFilterParams,
  defaultFolder,
} from 'components/projectsDashboard/helpers';
import useDocumentAllowance from 'components/projectsDashboard/hooks/useDocumentAllowance';

import { formatData } from 'components/helpers/reactQuery';
import { pluralizeProjectsName } from 'components/helpers/resources/projects';
import { resourcesAsResourceMap } from 'components/helpers/serialisableResources';

export default function ProjectsIndex({
  baseFilters,
  columnOrder,
  columnSizing,
  columnSort,
  columnVisibility,
  columns,
  defaultCurrentSearch,
  filters,
  folderId,
  hasFolders,
  onArchiveProjectsClick,
  onColumnOrderChange,
  onColumnSizingChange,
  onColumnSortChange,
  onColumnVisibilityChange,
  onDebouncedFiltersChange,
  onFilterAdd,
  onFilterRemove,
  onFilterValueAdd,
  onFilterValueChange,
  onFilterValueRemove,
  onGroupIntoFolderClick,
  onMoveToFolderClick,
  onProjectArchiveClose,
  onProjectDestroyClose,
  onProjectToggle,
  onProjectsFilterSidepanelClose,
  onProjectsPageChange,
  onResetTableToDefault,
  onSectionClick,
  onSelectAllProjectsToggle,
  selectedProjectIds,
  visibleFilters,
}) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const {
    folderPolicy,
    projectPolicy,
    projectsArchivePolicy,
    projectsBulkArchivePolicy,
    projectsBulkFolderAssignmentPolicy,
    projectsDuplicationProcessPolicy,
  } = useProjectsDashboardContext();

  const queryClient = useQueryClient();

  const [isEditColumnsSidePanelOpen, setIsEditColumnsSidePanelOpen] =
    useState(false);

  const [isProjectFilterSidePanelOpen, setIsProjectFilterSidePanelOpen] =
    useState(false);

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const [
    isResetTableToDefaultConfirmationModalOpen,
    setIsResetTableToDefaultConfirmationModalOpen,
  ] = useState(false);

  const [isEditingFolder, setIsEditingFolder] = useState(false);

  const { pluralRAMSName, singularRAMSName } = currentActor.division.attributes;

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsDashboard.components.ProjectsIndex',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const tabs = {
    all: i18nT('tabs.all', {
      interpolation: { escapeValue: false },
      pluralRAMSName,
    }),
    my: i18nT('tabs.my', {
      interpolation: { escapeValue: false },
      pluralRAMSName,
    }),
  };

  const [selectedTab, setSelectedTab] = useState(Object.keys(tabs)[0]);

  const [selectedSingularActionProjectId, setSelectedSingularActionProjectId] =
    useState(null);

  const [singularActionModalContext, setSingularActionModalContext] =
    useState(null); // Possible states: archive, destroy, duplicate

  const [
    isDocumentAllowanceLimitReachedModalOpen,
    setIsDocumentAllowanceLimitReachedModalOpen,
  ] = useState(false);

  const [currentFolder, setCurrentFolder, handleCurrentFolderInputChange] =
    useForm(defaultFolder);

  const [currentPage, setCurrentPage] = useState(1);

  const [currentSearch, setCurrentSearch, handleCurrentSearchInputChange] =
    useForm({ projectSearch: defaultCurrentSearch || '' });

  const [debouncedCurrentSearch] = useDebounce(currentSearch, 250, {
    onChange: () => setCurrentPage(1),
  });

  const [debouncedFilters] = useDebounce(filters, 250, {
    debounceIf: debounceFiltersIf,
    onChange: () => {
      onDebouncedFiltersChange();
      setCurrentPage(1);
    },
  });

  const folderNameInputRef = useRef(null);

  const createProjectArchiveMutation = useCreateProjectArchiveMutation();
  const createProjectRestorationMutation =
    useCreateProjectRestorationMutation();
  const destroyProjectMutation = useDestroyProjectMutation();
  const updateFolderMutation = useUpdateFolderMutation();
  const createFoldersUserSelectionMutation =
    useCreateFoldersUserSelectionMutation();

  const createProjectsDashboardsProjectsReportsExportProcessMutation =
    useCreateProjectsDashboardsProjectsReportsExportProcessMutation();

  const { handleDocumentAllowanceCheck, isDocumentAllowanceCheckPending } =
    useDocumentAllowance({
      onLimitNotReached: () => {
        window.location.href = `/projects/new${isScopedToFolder ? `?folder_id=${folderId}` : ''}`;
      },
      onLimitReached: () => setIsDocumentAllowanceLimitReachedModalOpen(true),
    });

  const selectedVisibleFilters = filters.selectedFilters.filter((filterKey) =>
    visibleFilters.includes(filterKey),
  );

  const selectedVisibleFiltersCount = selectedVisibleFilters.length;

  const filterParams = buildFilterParams({
    contributorId: selectedTab === 'my' ? currentActor.user.id : null,
    filters: debouncedFilters,
    folderId,
    visibleFilters,
  });

  const selectionsParams = buildSelectionsParams({
    columnVisibility,
    folderId,
  });

  const sortParams = buildSortParams({ columnSort });

  const dashboardReportParams = {
    filter: filterParams,
    search: debouncedCurrentSearch.projectSearch,
    selections: selectionsParams,
    sort: sortParams,
    page: currentPage,
  };

  const {
    data: domainDashboardReport,
    isError: isDomainDashboardReportFetchError,
    isFetching: isDomainDashboardReportFetching,
    isLoading: isDomainDashboardReportLoading,
    isSuccess: isDomainDashboardReportFetchSuccess,
  } = useProjectsDashboardsProjectsReportsDashboardReportQuery({
    params: dashboardReportParams,
    queryOptions: {
      keepPreviousData: true,
      select: (data) =>
        formatData({
          data,
          resourceTypes: [
            'field',
            'folder',
            'project',
            'projectContentReviewRequest',
            'user',
          ],
        }),
    },
  });

  const exportProcessId =
    createProjectsDashboardsProjectsReportsExportProcessMutation.data?.data.id;

  const {
    data: domainExportProcess,
    isError: isDomainExportProcessFetchError,
  } = useProjectsReportsExportProcessQuery({
    exportProcessId,
    queryOptions: { enabled: !!exportProcessId },
  });

  const isScopedToFolder = folderPolicy.canIndex && !!folderId;

  const { data: domainFolder } = useFolderQuery({
    id: folderId,
    queryOptions: { enabled: isScopedToFolder, keepPreviousData: true },
  });

  const { data: domainProjectDuplicationEligibility } = useQuery({
    enabled:
      projectsDuplicationProcessPolicy.canCreate &&
      !!selectedSingularActionProjectId,
    queryKey: [
      'projects',
      selectedSingularActionProjectId,
      'duplicationEligibility',
    ],
    queryFn: async () => {
      const response = await axios.get(
        `/projects/${selectedSingularActionProjectId}/duplication_eligibility`,
      );

      return response.data;
    },
    placeholderData: { data: { attributes: { blocked: true } } },
    staleTime: 300000, // Marking data as stale after 5 mins, as we need to have fairly recent data relating to any potential duplication blockers
  });

  const duplicationProcesses =
    queryClient.getQueryCache().find(['duplicationProcesses'])?.state?.data
      ?.data || [];

  const handleTabClick = (clickedTab) => {
    handleCurrentSearchInputReset();
    setCurrentPage(1);
    setSelectedTab(Object.keys(tabs).find((tab) => tabs[tab] === clickedTab));
  };

  const handleCurrentSearchInputReset = () => {
    setCurrentSearch({ ...currentSearch, projectSearch: '' });
  };

  const handleFolderNameClick = () => {
    setCurrentFolder({
      ...currentFolder,
      name: domainFolder.data.attributes.name,
    });

    setIsEditingFolder(true);
  };

  const handleInlineEditFolderNameSubmit = () => {
    handleUpdateFolderReset();

    if (currentFolder.name === domainFolder.data.attributes.name) {
      setIsEditingFolder(false);
    } else {
      updateFolderMutation.mutate(
        { folderParams: currentFolder, id: domainFolder.data.id },
        {
          onSuccess: () => {
            breadBoard.addToast(
              <SuccessToast
                message={i18nT('handleInlineEditFolderNameSubmit.success')}
                onBurnToast={breadBoard.handleBurnToast}
              />,
            );

            setIsEditingFolder(false);
          },
          onError: (error) => {
            if (error.response.status === 422) {
              folderNameInputRef.current.focus();
            } else {
              breadBoard.addInedibleToast({
                fullMessage: i18nT('handleInlineEditFolderNameSubmit.error'),
              });

              setIsEditingFolder(false);
            }
          },
        },
      );
    }
  };

  const handleUpdateFolderReset = () => {
    if (updateFolderMutation.isError) updateFolderMutation.reset();
  };

  const handleProjectsFilterSidePanelClose = () => {
    setIsProjectFilterSidePanelOpen(false);
    onProjectsFilterSidepanelClose();
  };

  const handleEditColumnsSidePanelClose = () => {
    setIsEditColumnsSidePanelOpen(false);
  };

  const handleResetTableToDefaultClick = () => {
    setIsResetTableToDefaultConfirmationModalOpen(false);
    onResetTableToDefault();
  };

  const handleProjectsPageChange = (event) => {
    onProjectsPageChange();

    setCurrentPage(Number(event.currentTarget.getAttribute('data-page')));
  };

  const handleProjectActionsClick = ({ id }) => {
    setSelectedSingularActionProjectId(id);
  };

  const handleProjectDuplicateClick = ({ id }) => {
    setSingularActionModalContext('duplicate');
    setSelectedSingularActionProjectId(id);
  };

  const handleProjectDuplicationModalJourneyClose = () => {
    setSingularActionModalContext(null);
    setSelectedSingularActionProjectId(null);
  };

  const handleProjectArchiveClick = ({ id }) => {
    setSingularActionModalContext('archive');
    setSelectedSingularActionProjectId(id);
  };

  const handleProjectArchiveClose = () => {
    setSingularActionModalContext(null);
    setSelectedSingularActionProjectId(null);
    onProjectArchiveClose();
  };

  const handleProjectDestroyClick = ({ id }) => {
    setSingularActionModalContext('destroy');
    setSelectedSingularActionProjectId(id);
  };

  const handleProjectDestroyClose = () => {
    setSingularActionModalContext(null);
    setSelectedSingularActionProjectId(null);
    onProjectDestroyClose();
  };

  const handleArchiveProjectSubmit = ({ id }) => {
    createProjectArchiveMutation.mutate(
      { id },
      {
        onSuccess: (data) => {
          handleProjectArchiveClose();

          const resourceMap = resourcesAsResourceMap({
            resources: data.included,
            resourceTypes: ['project'],
          });

          const project =
            resourceMap.project[data.data.relationships.project.data.id];

          breadBoard.addToast(
            <UndoableToast
              isUndoActionDisabled={createProjectRestorationMutation.isLoading}
              onBurnToast={breadBoard.handleBurnToast}
              onUndoAction={() => handleArchiveProjectUndo({ id })}
              toastText={
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='shared.success.archived'
                  ns='common'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{ name: project.attributes.name }}
                />
              }
            />,
          );
        },
        onError: (error) => {
          handleProjectArchiveClose();

          if (error.response.status === 422) {
            breadBoard.addInedibleToast({
              fullMessage: (
                <Trans
                  i18nKey='projectsDashboard.components.ProjectsIndex.handleArchiveProjectSubmit.error'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    projectName: error.response?.data?.meta?.projectName,
                  }}
                />
              ),
            });
          } else {
            breadBoard.addInedibleToast();
          }
        },
      },
    );
  };

  const handleArchiveProjectUndo = ({ id }) => {
    createProjectRestorationMutation.mutate(
      { id },
      {
        onSuccess: (data) => {
          const resourceMap = resourcesAsResourceMap({
            resources: data.included,
            resourceTypes: ['project'],
          });

          const project =
            resourceMap.project[data.data.relationships.project.data.id];

          breadBoard.addToast(
            <SuccessToast
              message={
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='shared.success.notArchived'
                  ns='common'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{ name: project.attributes.name }}
                />
              }
              onBurnToast={breadBoard.handleBurnToast}
            />,
          );
        },
        onError: breadBoard.addInedibleToast,
      },
    );
  };

  const handleDestroyProjectSubmit = ({ id }) => {
    destroyProjectMutation.mutate(
      { id },
      {
        onSuccess: (data) => {
          handleProjectDestroyClose();

          breadBoard.addToast(
            <SuccessToast
              message={
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='shared.success.deleted'
                  ns='common'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{ name: data.data.attributes.name }}
                />
              }
              onBurnToast={breadBoard.handleBurnToast}
            />,
          );
        },
        onError: (error) => {
          handleProjectDestroyClose();

          if (error.response.status === 422) {
            breadBoard.addInedibleToast({
              fullMessage: (
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='projectsDashboard.components.ProjectsIndex.handleDestroyProjectSubmit.error'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    projectName: error.response?.data?.meta?.projectName,
                  }}
                />
              ),
            });
          } else {
            breadBoard.addInedibleToast();
          }
        },
      },
    );
  };

  const handleExportClick = () => {
    setIsExportModalOpen(true);

    if (
      createProjectsDashboardsProjectsReportsExportProcessMutation.isIdle &&
      !isCustomFilterOrSearchApplied
    ) {
      handleCreateExportProcessSubmit({ projectSelection: 'unfiltered' });
    }
  };

  const handleCreateExportProcessSubmit = ({ projectSelection }) => {
    createProjectsDashboardsProjectsReportsExportProcessMutation.mutate(
      {
        exportProcessParams:
          projectSelection === 'filtered' ?
            {
              filter: dashboardReportParams.filter,
              search: dashboardReportParams.search,
            }
          : { filter: defaultFilterParams, search: '' },
      },
      {
        onError: () => {
          breadBoard.addInedibleToast();
          handleExportReset();
        },
      },
    );
  };

  const handleExportReset = () => {
    createProjectsDashboardsProjectsReportsExportProcessMutation.reset();
    setIsExportModalOpen(false);

    if (exportProcessId) {
      queryClient.removeQueries({
        queryKey: ['projectsReports', 'exportProcesses', exportProcessId],
        exact: true,
      });
    }
  };

  if (domainExportProcess?.data.attributes.status === 'completed') {
    saveAsFile(domainExportProcess.data.attributes.url);
    handleExportReset();
  }

  if (
    domainExportProcess?.data.attributes.status === 'failed' ||
    isDomainExportProcessFetchError
  ) {
    breadBoard.addInedibleToast();
    handleExportReset();
  }

  const favouriteUserFolderId =
    domainFolder ?
      domainFolder.data.relationships.favouriteUserFolder?.data?.id
    : null;

  const isSearchApplied = !!dashboardReportParams.search;

  const isCustomFilterOrSearchApplied =
    !_.isEqual(dashboardReportParams.filter, defaultFilterParams) ||
    isSearchApplied;

  const isMoveToFolderButtonDisabled = !(!!folderId || hasFolders);

  const selectedSingularActionProject =
    selectedSingularActionProjectId ?
      domainDashboardReport?.included.project[selectedSingularActionProjectId]
    : null;

  const isSearchFieldDisabled =
    isDomainDashboardReportFetchSuccess &&
    domainDashboardReport.data.relationships.projects.data.length === 0 &&
    !isSearchApplied;

  let updateFolderErrorMessage;
  if (
    updateFolderMutation.isError &&
    updateFolderMutation.error.response.status === 422
  ) {
    updateFolderErrorMessage =
      updateFolderMutation.error.response.data.errors[0].detail;
  }

  const isDataPending =
    isDomainDashboardReportLoading ||
    isDomainDashboardReportFetching ||
    isDomainDashboardReportFetchError;

  useEffect(() => {
    if (!folderId) return;

    createFoldersUserSelectionMutation.mutate({ folderId });
  }, [folderId]);

  useEffect(() => {
    if (!folderId) return;

    try {
      analytics.track('Folder viewed', {
        userId: currentActor.user.id,
        companyId: currentActor.division.id,
        accountId: currentActor.account.id,
        email: currentActor.user.attributes.email,
        folderId,
      });
    } catch (error) {
      console.error(error);
    }
  }, [folderId]);

  useEffect(() => {
    if (isDomainDashboardReportFetchError) onResetTableToDefault();
  }, [isDomainDashboardReportFetchError]);

  return (
    <>
      <div className='tw-flex tw-flex-col tw-gap-6'>
        <div className='tw-flex tw-flex-col tw-gap-1 tw-px-4'>
          <div className='tw-flex tw-h-10 tw-items-center tw-justify-between tw-gap-6'>
            {isScopedToFolder ?
              <div className='tw-flex tw-min-w-0 tw-items-center tw-gap-1'>
                <FavouriteFolderToggleButton
                  favouriteUserFolderId={favouriteUserFolderId}
                  folderId={folderId}
                />
                {folderPolicy.canUpdate ?
                  isEditingFolder ?
                    <ConditionalWrapper
                      condition={!!updateFolderErrorMessage}
                      wrapper={(children) => (
                        <Tooltip
                          placement='bottom'
                          tooltip={updateFolderErrorMessage}
                          trigger='focus'
                        >
                          {children}
                        </Tooltip>
                      )}
                    >
                      <div
                        className={classNames(
                          'tw-flex tw-w-61 tw-items-center tw-gap-0.5 tw-rounded-lg tw-border-1 tw-border-solid tw-px-1.5 tw-py-1',
                          updateFolderErrorMessage ?
                            'tw-border-red-300 tw-bg-red-025'
                          : 'tw-border-blue-300 tw-bg-white',
                        )}
                      >
                        <input
                          autoFocus={true}
                          className='tw-w-full tw-border-0 tw-bg-transparent tw-p-0 tw-text-xl tw-font-semibold tw-text-grey-900 tw-outline-none'
                          name='name'
                          onBlur={(event) => {
                            if (!event.relatedTarget) {
                              handleInlineEditFolderNameSubmit();
                            }
                          }}
                          onChange={(event) => {
                            handleUpdateFolderReset();
                            handleCurrentFolderInputChange(event);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              handleInlineEditFolderNameSubmit();
                            }
                          }}
                          ref={folderNameInputRef}
                          value={currentFolder.name}
                        />
                        {!!domainFolder &&
                          currentFolder.name !==
                            domainFolder.data.attributes.name && (
                            <button
                              className={classNames(
                                'tw-h-5 tw-w-5 tw-rounded-lg tw-border-0 tw-bg-transparent tw-p-0 [&_polygon]:tw-fill-grey-700',
                                updateFolderErrorMessage ?
                                  'hover:tw-bg-red-050'
                                : 'hover:tw-bg-blue-025',
                              )}
                              onClick={() => {
                                folderNameInputRef.current.focus();
                                handleUpdateFolderReset();

                                setCurrentFolder({
                                  ...currentFolder,
                                  name: domainFolder.data.attributes.name,
                                });
                              }}
                            >
                              <CloseIcon height={20} width={20} />
                              <span className='tw-sr-only'>
                                {i18nTCommon('shared.clear')}
                              </span>
                            </button>
                          )}
                      </div>
                    </ConditionalWrapper>
                  : <button
                      className='tw-group tw-flex tw-min-w-0 tw-items-center tw-gap-3 tw-rounded-lg tw-border-1 tw-border-solid tw-border-transparent tw-bg-transparent tw-py-1 tw-pl-1.5 tw-pr-2 hover:tw-cursor-pointer hover:tw-border-blue-300 hover:tw-bg-blue-025'
                      onClick={() => {
                        if (domainFolder) handleFolderNameClick();
                      }}
                    >
                      <p
                        className='tw-mb-0 tw-min-w-0 tw-flex-auto tw-truncate tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'
                        data-element-name='folder-name'
                      >
                        {domainFolder?.data.attributes.name}
                      </p>
                      <EditIcon
                        className='tw-flex-none tw-opacity-0 group-hover:tw-opacity-100 [&_path]:tw-fill-blue-400'
                        height={20}
                        width={20}
                      />
                    </button>

                : <p
                    className='tw-mb-0 tw-min-w-0 tw-flex-auto tw-truncate tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'
                    data-element-name='folder-name'
                  >
                    {domainFolder?.data.attributes.name}
                  </p>
                }
              </div>
            : <div className='tw-flex tw-items-center tw-gap-1'>
                <FilesIcon
                  className='[&_path]:tw-fill-grey-500'
                  height={20}
                  width={20}
                />
                <p className='tw-mb-0 tw-px-1.5 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
                  {i18nTCommon('projects.currentProjects', {
                    interpolation: { escapeValue: false },
                    pluralRAMSName: voca.capitalize(pluralRAMSName),
                  })}
                </p>
              </div>
            }
            {projectPolicy.canNew && (
              <FilledButton
                className='tw-flex-none'
                color='mint'
                data-element-name='new-project'
                disabled={isDocumentAllowanceCheckPending}
                onClick={handleDocumentAllowanceCheck}
              >
                <div className='tw-flex tw-gap-3'>
                  <AddIcon
                    className='-tw-ml-1 [&_polygon]:tw-fill-grey-900'
                    height={20}
                    width={20}
                  />
                  {i18nTCommon('projects.newProject', {
                    interpolation: { escapeValue: false },
                    singularRAMSName,
                  })}
                </div>
              </FilledButton>
            )}
          </div>
          <div className='tw-flex tw-gap-4'>
            {!isScopedToFolder && projectPolicy.canNew && (
              <SegmentedControls
                activeChoice={tabs[selectedTab]}
                choices={Object.values(tabs)}
                onItemClick={handleTabClick}
              />
            )}
            <SearchField
              additionalClasses={classNames(
                'tw-w-70',
                isSearchFieldDisabled ? 'tw-bg-grey-025' : 'tw-bg-white',
              )}
              isDisabled={isSearchFieldDisabled}
              name='projectSearch'
              onChange={handleCurrentSearchInputChange}
              onReset={handleCurrentSearchInputReset}
              placeholder={i18nT(
                `search.placeholder.${isScopedToFolder ? 'folders' : selectedTab}Tab`,
                {
                  interpolation: { escapeValue: false },
                  pluralRAMSName,
                },
              )}
              value={currentSearch.projectSearch}
            />
          </div>
        </div>
        {(
          isDomainDashboardReportFetchSuccess &&
          !isSearchApplied &&
          debouncedFilters.selectedFilters.length === 0 &&
          domainDashboardReport.data.relationships.projects.data.length === 0
        ) ?
          projectPolicy.canNew ?
            <EmptyTableState
              icon={NoteAdd}
              title={
                isScopedToFolder ?
                  i18nT('emptyState.inFolder.creatable.title', {
                    interpolation: { escapeValue: false },
                    pluralRAMSName,
                  })
                : i18nT('emptyState.creatable.title', {
                    interpolation: { escapeValue: false },
                    singularRAMSName,
                  })
              }
            >
              {isScopedToFolder ?
                <p className='tw-mb-0 tw-text-center tw-text-m tw-font-normal tw-text-grey-900'>
                  <Trans
                    components={{
                      a: (
                        /* eslint-disable jsx-a11y/anchor-has-content */
                        <a
                          className='tw-text-m tw-font-medium tw-text-blue-500'
                          href={`/projects/new?folder_id=${folderId}`}
                        />
                        /* eslint-enable jsx-a11y/anchor-has-content */
                      ),
                      currentButton: (
                        <button
                          className='tw-border-0 tw-bg-transparent tw-p-0 tw-text-m tw-font-medium tw-text-blue-500'
                          onClick={() => onSectionClick({ section: 'current' })}
                        />
                      ),
                      folderButton: (
                        <button
                          className='tw-border-0 tw-bg-transparent tw-p-0 tw-text-m tw-font-medium tw-text-blue-500'
                          onClick={() => onSectionClick({ section: 'folders' })}
                        />
                      ),
                    }}
                    i18nKey='projectsDashboard.components.ProjectsIndex.emptyState.inFolder.creatable.description'
                    ns='components'
                    tOptions={{ interpolation: { escapeValue: false } }}
                    values={{ pluralRAMSName, singularRAMSName }}
                  />
                </p>
              : <div className='tw-flex tw-flex-col tw-items-center tw-gap-4'>
                  {['administrator', 'primary'].includes(
                    currentActor.user.attributes.accessType,
                  ) && (
                    <p className='tw-mb-0 tw-text-center tw-text-m tw-font-normal tw-text-grey-900'>
                      <Trans
                        components={{
                          a: (
                            /* eslint-disable jsx-a11y/anchor-has-content */
                            <a
                              className='tw-text-m tw-font-medium tw-text-blue-500'
                              href='/settings/business/edit'
                            />
                            /* eslint-enable jsx-a11y/anchor-has-content */
                          ),
                        }}
                        i18nKey='projectsDashboard.components.ProjectsIndex.emptyState.creatable.description'
                        ns='components'
                      />
                    </p>
                  )}
                  <FilledButton
                    className='tw-flex-none'
                    color='mint'
                    href={`/projects/new${isScopedToFolder ? `?folder_id=${folderId}` : ''}`}
                  >
                    <div className='tw-flex tw-gap-3'>
                      <AddIcon
                        className='-tw-ml-1 [&_polygon]:tw-fill-grey-900'
                        height={20}
                        width={20}
                      />
                      {i18nTCommon('projects.newProject', {
                        interpolation: { escapeValue: false },
                        singularRAMSName,
                      })}
                    </div>
                  </FilledButton>
                </div>
              }
            </EmptyTableState>
          : <EmptyTableState
              icon={FilesIcon}
              title={i18nT(
                isScopedToFolder ?
                  'emptyState.inFolder.readOnly.title'
                : 'emptyState.readOnly.title',
                {
                  interpolation: { escapeValue: false },
                  pluralRAMSName,
                },
              )}
            >
              <p className='tw-mb-0 tw-text-center tw-text-m tw-font-normal tw-text-grey-900'>
                {i18nT('emptyState.readOnly.description', {
                  interpolation: { escapeValue: false },
                  pluralRAMSName,
                })}
              </p>
            </EmptyTableState>

        : <div className='tw-flex tw-flex-col tw-gap-2'>
            <div className='tw-flex tw-h-8 tw-items-center tw-justify-between tw-px-4'>
              <div className='tw-flex tw-items-center tw-gap-1'>
                {duplicationProcesses.length > 0 && (
                  <div
                    className='tw-apply-loading-spinner--blue-light tw-translate-y-[2px]'
                    data-element-name='duplication-in-progress-loader'
                  >
                    <Tooltip
                      placement='bottom'
                      tooltip={i18nT('duplicationInProgress', {
                        interpolation: { escapeValue: false },
                        singularRAMSName: voca.capitalize(singularRAMSName),
                      })}
                      trigger='hover'
                    >
                      <LoadingIcon height={20} width={20} />
                    </Tooltip>
                  </div>
                )}
                {(
                  (folderPolicy.canCreate ||
                    projectsBulkArchivePolicy.canCreate ||
                    projectsBulkFolderAssignmentPolicy.canCreate) &&
                  selectedProjectIds.length > 0
                ) ?
                  <div className='tw-flex tw-items-center tw-gap-6'>
                    <p className='tw-mb-0 tw-flex-none tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-700'>
                      {i18nT('selectedCount', {
                        count: selectedProjectIds.length,
                        interpolation: { escapeValue: false },
                        ramsName: pluralizeProjectsName({
                          count: selectedProjectIds.length,
                          currentActor,
                          shouldCapitalize: true,
                        }),
                      })}
                    </p>
                    <div className='tw-flex tw-items-center tw-gap-4'>
                      {projectsBulkFolderAssignmentPolicy.canCreate && (
                        <button
                          className={classNames(
                            'tw-flex tw-h-8 tw-flex-none tw-items-center tw-gap-2 tw-rounded-lg tw-border-1 tw-border-solid tw-pl-2.5 tw-pr-4 tw-text-m tw-font-medium',
                            isMoveToFolderButtonDisabled ?
                              'tw-border-grey-100 tw-bg-grey-050 tw-text-grey-300'
                            : 'tw-border-blue-050 tw-bg-blue-025 tw-text-grey-900 hover:tw-border-blue-100 hover:tw-bg-blue-050 active:tw-border-blue-200 active:tw-bg-blue-100',
                          )}
                          disabled={isMoveToFolderButtonDisabled}
                          onClick={onMoveToFolderClick}
                          type='button'
                        >
                          <DriveFileMoveIcon
                            className={classNames(
                              isMoveToFolderButtonDisabled ?
                                '[&_path]:tw-fill-grey-300'
                              : '[&_path]:tw-fill-grey-700',
                            )}
                            height={20}
                            width={20}
                          />
                          {i18nT('bulkActions.move')}
                        </button>
                      )}
                      {folderPolicy.canCreate && (
                        <button
                          className='tw-flex tw-h-8 tw-flex-none tw-items-center tw-gap-2 tw-rounded-lg tw-border-1 tw-border-solid tw-border-blue-050 tw-bg-blue-025 tw-pl-2.5 tw-pr-4 tw-text-m tw-font-medium tw-text-grey-900 hover:tw-border-blue-100 hover:tw-bg-blue-050 active:tw-border-blue-200 active:tw-bg-blue-100'
                          onClick={onGroupIntoFolderClick}
                          type='button'
                        >
                          <CreateNewFolderIcon
                            className='[&_path]:tw-fill-grey-700'
                            height={20}
                            width={20}
                          />
                          {i18nT('bulkActions.group')}
                        </button>
                      )}
                      {projectsBulkArchivePolicy.canCreate && (
                        <button
                          className='tw-flex tw-h-8 tw-flex-none tw-items-center tw-gap-2 tw-rounded-lg tw-border-1 tw-border-solid tw-border-blue-050 tw-bg-blue-025 tw-pl-2.5 tw-pr-4 tw-text-m tw-font-medium tw-text-grey-900 hover:tw-border-blue-100 hover:tw-bg-blue-050 active:tw-border-blue-200 active:tw-bg-blue-100'
                          onClick={onArchiveProjectsClick}
                          type='button'
                        >
                          <ArchiveIcon
                            className='[&_path]:tw-fill-grey-700'
                            height={20}
                            width={20}
                          />
                          {i18nTCommon('shared.archive')}
                        </button>
                      )}
                    </div>
                  </div>
                : <p className='tw-mb-0 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-700'>
                    {pluralizeProjectsName({
                      count: domainDashboardReport?.meta.scopedCount || 0,
                      currentActor,
                      shouldCapitalize: true,
                      shouldIncludeCount: true,
                    })}
                  </p>
                }
              </div>
              <div className='tw-flex tw-items-center tw-gap-4'>
                {selectedVisibleFiltersCount === 0 ?
                  <Options
                    btnToggle={(isOpen) => (
                      <button
                        className={classNames(
                          'tw-flex tw-h-8 tw-flex-none tw-items-center tw-gap-2 tw-rounded-lg tw-border-1 tw-border-solid tw-border-blue-050 tw-bg-blue-025 tw-pl-2.5 tw-pr-1.5 tw-text-m tw-font-medium tw-text-grey-900 hover:tw-border-blue-100 hover:tw-bg-blue-050 active:tw-border-blue-200 active:tw-bg-blue-100',
                          isOpen && 'tw-border-blue-100 tw-bg-blue-050',
                        )}
                        data-element-name='filter-options'
                        onClick={() => {}}
                        type='button'
                      >
                        <FunnelIcon height={12} width={12} />
                        <span>{i18nTCommon('shared.filter')}</span>
                        <ChevronUpIcon
                          height={20}
                          style={{ display: isOpen ? 'inline-block' : 'none' }}
                          width={20}
                        />
                        <ChevronDownIcon
                          height={20}
                          style={{ display: isOpen ? 'none' : 'inline-block' }}
                          width={20}
                        />
                      </button>
                    )}
                    listStyle='tw-w-52 [&_li]:tw-border-none tw-z-[1000]'
                    toggleStyle='tw-inline-flex [&_ul]:tw-border-blue-100 [&_ul]:tw-rounded-lg'
                  >
                    {visibleFilters.map(
                      (filterKey) =>
                        !selectedVisibleFilters.includes(filterKey) && (
                          <OptionChoice
                            key={filterKey}
                            onClick={(setOptionsOpen) => {
                              setOptionsOpen(false);
                              onFilterAdd(filterKey);
                              setIsProjectFilterSidePanelOpen(true);
                            }}
                          >
                            <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
                              {baseFilters.get(filterKey)}
                            </a>
                          </OptionChoice>
                        ),
                    )}
                  </Options>
                : <button
                    className='tw-group/button tw-flex tw-h-8 tw-flex-none tw-items-center tw-gap-2 tw-rounded-lg tw-border-1 tw-border-solid tw-border-blue-050 tw-bg-blue-025 tw-pl-2.5 tw-pr-1.5 tw-text-m tw-font-medium tw-text-grey-900 hover:tw-border-blue-100 hover:tw-bg-blue-050 active:tw-border-blue-200 active:tw-bg-blue-100'
                    data-element-name='filter-options'
                    onClick={() => setIsProjectFilterSidePanelOpen(true)}
                    type='button'
                  >
                    <FunnelIcon height={12} width={12} />
                    <span>
                      {selectedVisibleFiltersCount === 1 ?
                        i18nTCommon('shared.filter')
                      : i18nTCommon('shared.filters')}
                    </span>
                    <span className='tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-050 tw-text-s tw-font-medium tw-tracking-wide tw-text-blue-900 group-hover/button:tw-bg-blue-100 group-active/button:tw-bg-blue-200'>
                      {selectedVisibleFiltersCount}
                    </span>
                  </button>
                }
                <Options
                  btnToggle={(isOpen) => (
                    <button
                      className={classNames(
                        'tw-flex tw-h-8 tw-flex-none tw-items-center tw-gap-2 tw-rounded-lg tw-border-1 tw-border-solid tw-border-blue-050 tw-bg-blue-025 tw-pl-2.5 tw-pr-1.5 tw-text-m tw-font-medium tw-text-grey-900 hover:tw-border-blue-100 hover:tw-bg-blue-050 active:tw-border-blue-200 active:tw-bg-blue-100',
                        isOpen && 'tw-border-blue-100 tw-bg-blue-050',
                      )}
                      data-element-name='column-options'
                      onClick={() => {}}
                      type='button'
                    >
                      <TableIcon height={14} width={14} />
                      <ChevronUpIcon
                        height={20}
                        style={{ display: isOpen ? 'inline-block' : 'none' }}
                        width={20}
                      />
                      <ChevronDownIcon
                        height={20}
                        style={{ display: isOpen ? 'none' : 'inline-block' }}
                        width={20}
                      />
                    </button>
                  )}
                  listStyle='tw-w-52 [&_li]:tw-border-none tw-z-[1000]'
                  toggleStyle='tw-inline-flex [&_ul]:tw-border-blue-100 [&_ul]:tw-rounded-lg'
                >
                  <OptionChoice
                    onClick={(setOptionsOpen) => {
                      handleExportClick();
                      setOptionsOpen(false);
                    }}
                  >
                    <a className='tw-text-wrap tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
                      {i18nTCommon('shared.export')}
                    </a>
                  </OptionChoice>
                  <OptionChoice
                    onClick={(setOptionsOpen) => {
                      setOptionsOpen(false);
                      setIsEditColumnsSidePanelOpen(true);
                    }}
                  >
                    <a className='tw-text-wrap tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
                      {i18nTCommon('shared.editColumns')}
                    </a>
                  </OptionChoice>
                  <OptionChoice
                    onClick={(setOptionsOpen) => {
                      setOptionsOpen(false);
                      setIsResetTableToDefaultConfirmationModalOpen(true);
                    }}
                  >
                    <a className='tw-text-wrap tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
                      {i18nT('columns.options.resetToDefault')}
                    </a>
                  </OptionChoice>
                </Options>
              </div>
            </div>
            {(
              isDataPending ||
              domainDashboardReport.data.relationships.projects.data.length > 0
            ) ?
              <>
                <ProjectsTable
                  columnOrder={columnOrder}
                  columnSizing={columnSizing}
                  columnSort={columnSort}
                  columnVisibility={columnVisibility}
                  columns={columns}
                  domainDashboardReport={domainDashboardReport}
                  folderId={folderId}
                  isDataPending={isDataPending}
                  isProjectDuplicationBlocked={
                    domainProjectDuplicationEligibility.data.attributes.blocked
                  }
                  onColumnSizingChange={onColumnSizingChange}
                  onColumnSortChange={onColumnSortChange}
                  onDocumentAllowanceLimitReached={() =>
                    setIsDocumentAllowanceLimitReachedModalOpen(true)
                  }
                  onProjectActionsClick={handleProjectActionsClick}
                  onProjectArchiveClick={handleProjectArchiveClick}
                  onProjectDestroyClick={handleProjectDestroyClick}
                  onProjectDuplicateClick={handleProjectDuplicateClick}
                  onProjectToggle={onProjectToggle}
                  onSelectAllProjectsToggle={onSelectAllProjectsToggle}
                  selectedProjectIds={selectedProjectIds}
                />
                {isDomainDashboardReportFetchSuccess &&
                  domainDashboardReport.meta.totalPages > 1 && (
                    <div className='tw-mt-12 tw-text-center'>
                      <Paginator
                        currentPage={domainDashboardReport.meta.currentPage}
                        onClick={handleProjectsPageChange}
                        totalPages={domainDashboardReport.meta.totalPages}
                      />
                    </div>
                  )}
              </>
            : <EmptyTableState
                icon={SearchOffIcon}
                title={
                  isSearchApplied ?
                    i18nTCommon('projects.noProjectsFound', {
                      interpolation: { escapeValue: false },
                      pluralRAMSName,
                    })
                  : i18nT('noResults.filters.title', {
                      interpolation: { escapeValue: false },
                      pluralRAMSName,
                    })
                }
              >
                <p className='tw-mb-0 tw-text-center tw-text-m tw-font-normal tw-text-grey-900'>
                  {isSearchApplied ?
                    debouncedFilters.selectedFilters.length > 0 ?
                      <Trans
                        components={{
                          archiveButton: (
                            <button
                              className='tw-border-0 tw-bg-transparent tw-p-0 tw-text-m tw-font-medium tw-text-blue-500'
                              onClick={() =>
                                onSectionClick({ section: 'archive' })
                              }
                            />
                          ),
                          editButton: (
                            <button
                              className='tw-border-0 tw-bg-transparent tw-p-0 tw-text-m tw-font-medium tw-text-blue-500'
                              onClick={() => {
                                setIsProjectFilterSidePanelOpen(true);
                              }}
                            />
                          ),
                        }}
                        i18nKey='projectsDashboard.components.ProjectsIndex.noResults.search.description.withFilters'
                        ns='components'
                        tOptions={{ interpolation: { escapeValue: false } }}
                        values={{ singularRAMSName }}
                      />
                    : <Trans
                        components={{
                          button: (
                            <button
                              className='tw-border-0 tw-bg-transparent tw-p-0 tw-text-m tw-font-medium tw-text-blue-500'
                              onClick={() =>
                                onSectionClick({ section: 'archive' })
                              }
                            />
                          ),
                        }}
                        i18nKey='projectsDashboard.components.ProjectsIndex.noResults.search.description.withoutFilters'
                        ns='components'
                        tOptions={{ interpolation: { escapeValue: false } }}
                        values={{ singularRAMSName }}
                      />

                  : <Trans
                      components={{
                        button: (
                          <button
                            className='tw-border-0 tw-bg-transparent tw-p-0 tw-text-m tw-font-medium tw-text-blue-500'
                            onClick={() =>
                              setIsProjectFilterSidePanelOpen(true)
                            }
                          />
                        ),
                      }}
                      i18nKey='projectsDashboard.components.ProjectsIndex.noResults.filters.description'
                      ns='components'
                    />
                  }
                </p>
              </EmptyTableState>
            }
            {isExportModalOpen && (
              <ExportProjectsModal
                isFilteredSelectionDisabled={
                  isCustomFilterOrSearchApplied &&
                  domainDashboardReport.data.relationships.projects.data
                    .length === 0
                }
                isGenerating={
                  !createProjectsDashboardsProjectsReportsExportProcessMutation.isIdle
                }
                onClose={() => setIsExportModalOpen(false)}
                onSubmit={handleCreateExportProcessSubmit}
              />
            )}
          </div>
        }
      </div>
      <ProjectsFilterSidePanel
        closeSidePanel={handleProjectsFilterSidePanelClose}
        filters={filters}
        isSidePanelOpen={isProjectFilterSidePanelOpen}
        onFilterAdd={onFilterAdd}
        onFilterRemove={onFilterRemove}
        onFilterValueAdd={onFilterValueAdd}
        onFilterValueChange={onFilterValueChange}
        onFilterValueRemove={onFilterValueRemove}
        visibleFilters={visibleFilters}
      />
      <EditColumnsSidePanel
        closeSidePanel={handleEditColumnsSidePanelClose}
        columnOrder={columnOrder}
        columnVisibility={columnVisibility}
        folderId={folderId}
        isSidePanelOpen={isEditColumnsSidePanelOpen}
        onColumnOrderChange={onColumnOrderChange}
        onColumnVisibilityChange={onColumnVisibilityChange}
      />
      <ResetTableToDefaultConfirmationModal
        isOpen={isResetTableToDefaultConfirmationModalOpen}
        onClose={() => setIsResetTableToDefaultConfirmationModalOpen(false)}
        onResetTableToDefaultClick={handleResetTableToDefaultClick}
      />
      {projectsDuplicationProcessPolicy.canCreate && (
        <ProjectDuplicationManager
          isModalOpen={singularActionModalContext === 'duplicate'}
          onCloseModalJourney={handleProjectDuplicationModalJourneyClose}
          project={selectedSingularActionProject}
        />
      )}
      {projectsArchivePolicy.canCreate &&
        singularActionModalContext === 'archive' &&
        selectedSingularActionProject && (
          <ArchiveProjectModal
            isArchiveProjectDisabled={createProjectArchiveMutation.isLoading}
            onArchiveProjectSubmit={handleArchiveProjectSubmit}
            onClose={() => setSingularActionModalContext(null)}
            project={selectedSingularActionProject}
          />
        )}
      {projectPolicy.canDestroy &&
        singularActionModalContext === 'destroy' &&
        selectedSingularActionProject && (
          <DestroyProjectModal
            isDestroyProjectDisabled={destroyProjectMutation.isLoading}
            onClose={() => setSingularActionModalContext(null)}
            onDestroyProjectSubmit={handleDestroyProjectSubmit}
            project={selectedSingularActionProject}
          />
        )}
      {isDocumentAllowanceLimitReachedModalOpen && (
        <DocumentAllowanceLimitReachedModal
          onClose={() => setIsDocumentAllowanceLimitReachedModalOpen(false)}
        />
      )}
    </>
  );
}

ProjectsIndex.propTypes = {
  baseFilters: PropTypes.object.isRequired,
  columnOrder: PropTypes.array.isRequired,
  columnSizing: PropTypes.object.isRequired,
  columnSort: PropTypes.object.isRequired,
  columnVisibility: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  defaultCurrentSearch: PropTypes.string,
  filters: PropTypes.object.isRequired,
  folderId: PropTypes.string,
  hasFolders: PropTypes.bool.isRequired,
  onArchiveProjectsClick: PropTypes.func.isRequired,
  onColumnOrderChange: PropTypes.func.isRequired,
  onColumnSizingChange: PropTypes.func.isRequired,
  onColumnSortChange: PropTypes.func.isRequired,
  onColumnVisibilityChange: PropTypes.func.isRequired,
  onDebouncedFiltersChange: PropTypes.func.isRequired,
  onFilterAdd: PropTypes.func.isRequired,
  onFilterRemove: PropTypes.func.isRequired,
  onFilterValueAdd: PropTypes.func.isRequired,
  onFilterValueChange: PropTypes.func.isRequired,
  onFilterValueRemove: PropTypes.func.isRequired,
  onGroupIntoFolderClick: PropTypes.func.isRequired,
  onMoveToFolderClick: PropTypes.func.isRequired,
  onProjectArchiveClose: PropTypes.func.isRequired,
  onProjectDestroyClose: PropTypes.func.isRequired,
  onProjectToggle: PropTypes.func.isRequired,
  onProjectsFilterSidepanelClose: PropTypes.func.isRequired,
  onProjectsPageChange: PropTypes.func.isRequired,
  onResetTableToDefault: PropTypes.func.isRequired,
  onSectionClick: PropTypes.func.isRequired,
  onSelectAllProjectsToggle: PropTypes.func.isRequired,
  selectedProjectIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  visibleFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export function debounceFiltersIf({ currentValue, prevValue }) {
  const valueDiff = diff(prevValue, currentValue);

  return !!(
    valueDiff.lastUpdatedFilter?.withinTheLastFrequency ||
    valueDiff.reviewDateFilter?.withinTheNextFrequency ||
    valueDiff.reviewDateFilter?.withinTheLastFrequency ||
    valueDiff.creationDateFilter?.withinTheLastFrequency ||
    valueDiff.startDateFilter?.withinTheNextFrequency ||
    valueDiff.startDateFilter?.withinTheLastFrequency
  );
}
